<script lang="ts">
interface Props {
    modelValue: boolean
    packagingOrderId?: uuid
}
</script>

<script setup lang="ts">
import type { FileAttachment, MinimalResource, ResourceResponse, uuid } from '@/types/general'

import axios, { Method } from 'axios'
import { watch, computed, ref, onBeforeMount } from 'vue'
import { useI18n } from 'vue-i18n'
import { promiseTimeout } from '@vueuse/core'
import Datepicker from '@vuepic/vue-datepicker'
import dayjs from 'dayjs'

import useForm from '@/hooks/use-form'
import { useAuthStore } from '@/stores/auth-store'
import { DropdownStringOption } from '@/types/inputs'
import { type CompanyProduct, PackagingOrder, PackagingOrderType } from '@/types/packaging'
import { emitter } from '@/utils/mitt'
import { useFileUploader } from '@/hooks/use-file-uploader'
import { TransactionProduct } from '@/types/transaction'
import { useProductStore } from '@/stores/product-store'
import { MinimalCompany } from '@/types/company'
import { useCompanyStore } from '@/stores/company-store'

import MySearchInput from '@/components/my-components/form/MySearchInput.vue'
import MyErrorMessage from '@/components/my-components/form/MyErrorMessage.vue'
import ManageTransactionProductItem from '@/components/packaging/transactions/manage/ManageTransactionProductItem.vue'
import MyFileAttachment from '@/components/my-components/MyFileAttachment.vue'
import MyFileSelect from '@/components/my-components/MyFileSelect.vue'
import MyTextarea from '@/components/my-components/form/MyTextarea.vue'
import MyRadioButtonGroup from '@/components/my-components/form/MyRadioButtonGroup.vue'
import MyRadioButton from '@/components/my-components/form/MyRadioButton.vue'
import MySelect from '@/components/my-components/form/MySelect.vue'
import LoaderWrapper from '@/components/loaders/LoaderWrapper.vue'
import MyButton from '@/components/my-components/MyButton.vue'
import MyModal from '@/components/my-components/MyModal.vue'
import MyForm from '@/components/my-components/form/MyForm.vue'
import MyInputLabel from '@/components/my-components/form/MyInputLabel.vue'
import MyCheckbox from '@/components/my-components/form/MyCheckbox.vue'

enum PackagingCompanyType {
    Supplier = 0,
    Customer = 1,
}

interface Form {
    customerCompanyId: uuid | null
    customerLocationId: uuid | null
    supplierCompanyId: uuid | null
    supplierLocationId: uuid | null
    type: PackagingOrderType
    products: TransactionProduct[]
    comment: string
    plannedAt: string
    files: FileAttachment[]
}

const props = defineProps<Props>()
const emit = defineEmits<{
    (e: 'close'): void
    (e: 'saved'): void
}>()

const authStore = useAuthStore()
const productStore = useProductStore()
const companyStore = useCompanyStore()
const { t } = useI18n()

const suppliers = ref<DropdownStringOption[]>([])
const supplierLocations = ref<DropdownStringOption[]>([])
const customers = ref<DropdownStringOption[]>([])
const customerLocations = ref<DropdownStringOption[]>([])
const packagingOrder = ref<PackagingOrder>()
const packagingCompanyType = ref<PackagingCompanyType>(PackagingCompanyType.Supplier)
const initializing = ref(false)
const searchQuery = ref('')
const showAllProducts = ref(false)
const supplierProducts = ref<CompanyProduct[]>([])

const { filesUploading, uploadFiles } = useFileUploader({
    addFile(file) {
        data.files.push(file)
    },
    updateFile(fileId, path) {
        data.files = data.files.map((file) => {
            if (file.id === fileId) return { ...file, path }

            return file
        })
    },
})

const { data, submit, errors, reset, loading } = useForm<Form>({
    customerCompanyId: null,
    customerLocationId: null,
    supplierCompanyId: null,
    supplierLocationId: null,
    type: PackagingOrderType.Delivery,
    products: [],
    comment: '',
    plannedAt: dayjs().toISOString(),
    files: [],
})

const filteredProducts = computed<TransactionProduct[]>(() => {
    if (!searchQuery.value) return data.products

    const query = searchQuery.value.toLowerCase()

    // We filter by transactionProducts instead of data.products to ensure we are searching through all products
    return productStore.transactionProducts(data.products, true).filter((product) => {
        return product.name.toLowerCase().includes(query.toLowerCase())
    })
})

const hasSuppliers = computed(() => {
    return suppliers.value.length > 0
})

const hasCustomers = computed(() => {
    return customers.value.length > 0
})

async function fetchPackagingOrder() {
    const response = await axios.get<ResourceResponse<PackagingOrder>>(
        window.route('packaging.company.packaging-orders.show', {
            company: authStore.companyId,
            packaging_order: props.packagingOrderId ?? '',
        }),
    )
    packagingOrder.value = response.data.data

    if (packagingOrder.value.customerCompany.id === authStore.companyId) {
        packagingCompanyType.value = PackagingCompanyType.Customer
    } else {
        packagingCompanyType.value = PackagingCompanyType.Supplier
    }

    const supplierProducts = await productStore.companyProductRequest(
        packagingOrder.value.supplierCompany.id,
    )

    reset({
        customerCompanyId: packagingOrder.value.customerCompany.id,
        customerLocationId: packagingOrder.value.customerLocation?.id ?? null,
        supplierCompanyId: packagingOrder.value.supplierCompany.id,
        supplierLocationId: packagingOrder.value.supplierLocation?.id ?? null,
        type: packagingOrder.value.type,
        plannedAt: packagingOrder.value.plannedAt,
        products: productStore.transactionProducts(
            packagingOrder.value.products,
            showAllProducts.value,
            supplierProducts.data.data,
        ),
    })
}

async function fetchPackagingSuppliers() {
    const response = await axios.get<{ customers: MinimalCompany[]; clients: MinimalCompany[] }>(
        window.route('packaging.company.packaging-suppliers.index', {
            company: authStore.companyId,
        }),
    )
    // Customers that are marked as packaging supplier
    suppliers.value = response.data.customers.map((supplier) => ({
        value: supplier.id,
        label: supplier.name,
    }))
    // Clients that have marked us as packaging supplier
    customers.value = response.data.clients.map((customer) => ({
        value: customer.id,
        label: customer.name,
    }))
}

async function fetchLocations(id: uuid): Promise<DropdownStringOption[]> {
    const response = await axios.get<MinimalResource[]>(
        window.route('minimal.companies.locations', {
            company: id,
        }),
    )

    return response.data.map((location) => ({ value: location.id, label: location.name }))
}

async function newPackagingOrder() {
    // If we are marked as a supplier for some customers, but we don't have any packaging suppliers
    if (!hasCustomers.value && hasSuppliers.value) {
        packagingCompanyType.value = PackagingCompanyType.Customer

        reset({
            supplierCompanyId: null,
            customerCompanyId: authStore.companyId,
            customerLocationId: customerLocations.value[0]?.value ?? null,
        })
    }
    // If we have packaging suppliers, but we are not marked as a supplier for any customers
    else if (!hasSuppliers.value && hasCustomers.value) {
        packagingCompanyType.value = PackagingCompanyType.Supplier

        reset({
            customerCompanyId: null,
            supplierCompanyId: authStore.companyId,
            supplierLocationId: supplierLocations.value[0]?.value ?? null,
        })
    }
    // If we have both suppliers and customers, default to supplier
    else {
        packagingCompanyType.value = PackagingCompanyType.Supplier

        reset({
            supplierCompanyId: authStore.companyId,
            supplierLocationId: supplierLocations.value[0]?.value ?? null,
        })
    }

    setProducts(true)
}

async function packagingOrderCompanyTypeChanged() {
    await promiseTimeout(50)

    if (packagingCompanyType.value === PackagingCompanyType.Supplier) {
        reset({
            customerCompanyId: null,
            customerLocationId: null,
            supplierCompanyId: authStore.companyId,
            products: data.products,
        })
    } else {
        reset({
            customerCompanyId: authStore.companyId,
            supplierCompanyId: null,
            supplierLocationId: null,
            products: data.products,
        })
    }
    setProducts()
}

async function onModalOpen() {
    if (!props.modelValue) return
    initializing.value = true
    const promises = [companyStore.fetchCompany(), productStore.fetchProducts()]
    if (!hasCustomers.value && !hasSuppliers.value) {
        promises.push(fetchPackagingSuppliers())
    }
    // We'll have to wait for all data to be fetched before we can handle the packaging order
    await Promise.all(promises)

    if (props.packagingOrderId) {
        await fetchPackagingOrder()
    } else {
        await newPackagingOrder()
    }

    initializing.value = false
}

async function onSubmit() {
    let packagingOrderRoute = window.route('packaging.company.packaging-orders.store', {
        company: authStore.companyId,
    })
    if (props.packagingOrderId) {
        packagingOrderRoute = window.route('packaging.company.packaging-orders.update', {
            company: authStore.companyId,
            packaging_order: props.packagingOrderId,
        })
    }

    const transformedData = {
        ...data,
        products: data.products
            // Remove products where delivered and received are both 0
            .filter((product) => product.delivered > 0 || product.received > 0),
    }

    const method: Method = props.packagingOrderId ? 'PUT' : 'POST'
    const response = await submit<ResourceResponse<PackagingOrder>>(method, packagingOrderRoute, {
        data: transformedData,
    })

    if (response !== undefined) {
        emit('close')
        emit('saved')
        emitter.emit('fetchPackagingOrders')
    }
}

function removeFile(file: FileAttachment) {
    reset({ files: data.files.filter((f) => f.id !== file.id) })
}

function setProducts(clearProducts = false) {
    data.products = productStore.transactionProducts(
        clearProducts ? [] : data.products,
        showAllProducts.value,
        packagingCompanyType.value !== PackagingCompanyType.Supplier
            ? supplierProducts.value
            : undefined,
    )
}

watch(
    () => data.customerCompanyId,
    async (customer) => {
        if (!customer) return
        customerLocations.value = await fetchLocations(customer)
        if (packagingCompanyType.value === PackagingCompanyType.Customer) {
            data.customerLocationId = customerLocations.value[0]?.value
        }
    },
)

watch(
    () => data.supplierCompanyId,
    async (supplier) => {
        if (!supplier) return
        supplierLocations.value = await fetchLocations(supplier)

        if (packagingCompanyType.value === PackagingCompanyType.Supplier) {
            data.supplierLocationId = supplierLocations.value[0]?.value
        } else {
            const response = await productStore.companyProductRequest(supplier)
            supplierProducts.value = response.data.data
        }
        setProducts(true)
    },
)

watch(() => props.modelValue, onModalOpen)
watch(showAllProducts, () => setProducts())

onBeforeMount(onModalOpen)
</script>

<template>
    <MyModal :value="props.modelValue" :max-width="650" @close="emit('close')">
        <LoaderWrapper :visible="loading || initializing" class="rounded-xl" />
        <template #title>
            <h1 v-if="!initializing" class="block font-bold">
                {{ props.packagingOrderId ? t('packagingOrder') : t('createPackagingOrder') }}
            </h1>
        </template>
        <MyForm v-if="!initializing" :errors="errors" class="space-y-2" @submit.prevent="onSubmit">
            <div class="flex w-full space-x-5">
                <MyRadioButtonGroup
                    v-if="hasSuppliers && hasCustomers"
                    v-model="packagingCompanyType"
                    class="w-1/2"
                    :label="t('createPackagingOrderAs')"
                    @input="packagingOrderCompanyTypeChanged"
                >
                    <MyRadioButton
                        v-if="customers.length > 0"
                        :label="t('supplier')"
                        :value="PackagingCompanyType.Supplier"
                    />
                    <MyRadioButton
                        v-if="suppliers.length > 0"
                        :label="t('customer')"
                        :value="PackagingCompanyType.Customer"
                    />
                </MyRadioButtonGroup>

                <MyRadioButtonGroup
                    v-model="data.type"
                    class="w-1/2"
                    :class="{ 'pr-2.5': !hasSuppliers && !hasCustomers }"
                    name="type"
                    :label="t('type')"
                >
                    <MyRadioButton :label="t('delivery')" :value="PackagingOrderType.Delivery" />
                    <MyRadioButton :label="t('pickup')" :value="PackagingOrderType.Pickup" />
                </MyRadioButtonGroup>
            </div>

            <div class="flex w-full space-x-5">
                <MySelect
                    v-if="packagingCompanyType === PackagingCompanyType.Customer"
                    v-model="data.supplierCompanyId"
                    name="supplierCompanyId"
                    group-class="w-1/2"
                    :options="suppliers"
                    :label="t('supplier')"
                    searchable
                />
                <div v-else class="w-1/2">
                    <MyInputLabel v-text="t('supplier')" />
                    <h4
                        class="mt-0 flex h-10 cursor-default items-center rounded-xl border border-primary-200 pl-2 text-sm font-semibold text-stone-600 dark:border-dark-400 dark:text-primary-200"
                        v-text="authStore.company.name"
                    />
                </div>

                <MySelect
                    v-model="data.supplierLocationId"
                    name="supplierLocationId"
                    group-class="w-1/2"
                    :options="supplierLocations"
                    :disabled="data.supplierCompanyId === null"
                    :label="t('supplierLocation')"
                    searchable
                />
            </div>

            <div class="flex w-full space-x-5">
                <MySelect
                    v-if="packagingCompanyType === PackagingCompanyType.Supplier"
                    v-model="data.customerCompanyId"
                    name="customerCompanyId"
                    group-class="w-1/2"
                    :options="customers"
                    :label="t('customer')"
                    searchable
                />
                <div v-else class="w-1/2">
                    <MyInputLabel v-text="t('customer')" />
                    <h4
                        class="mt-0 flex h-10 cursor-default items-center rounded-xl border border-primary-200 pl-2 text-sm font-semibold text-stone-600 dark:border-dark-400 dark:text-primary-200"
                        v-text="authStore.company.name"
                    />
                </div>

                <MySelect
                    v-model="data.customerLocationId"
                    name="customerLocationId"
                    group-class="w-1/2"
                    :options="customerLocations"
                    :label="t('customerLocation')"
                    :disabled="data.customerCompanyId === null"
                    searchable
                />
            </div>

            <div class="flex flex-col w-1/2 pr-2.5 space-y-2">
                <MyInputLabel
                    v-text="
                        t(data.type === PackagingOrderType.Delivery ? 'deliveryDate' : 'pickupDate')
                    "
                />
                <Datepicker
                    v-model="data.plannedAt"
                    class="input-field rounded-xl border border-stone-300"
                    name="plannedAt"
                    close-on-scroll
                    auto-apply
                    auto-position
                    position="left"
                    :clearable="false"
                    enable-time-picker
                    format="yyyy-MM-dd HH:mm"
                    :transitions="false"
                    :placeholder="t('selectDate')"
                />
            </div>

            <MySearchInput v-model="searchQuery" group-class="pt-3 pb-1" />

            <div
                v-if="filteredProducts.length !== 0"
                class="flex justify-between text-center text-xs mt-3 mb-2"
            >
                <div
                    class="inline-block cursor-pointer pl-2 text-xs font-semibold uppercase"
                    v-text="t('products')"
                />
                <div class="font-semibold uppercase pr-4" v-text="t('amount')" />
            </div>
            <MyErrorMessage input-name="products" :label="t('products')" />

            <div>
                <ManageTransactionProductItem
                    v-for="(product, index) in filteredProducts"
                    :key="product.id"
                    v-model="filteredProducts[index]"
                    :hide-delivered="data.type === PackagingOrderType.Pickup"
                    :hide-received="data.type === PackagingOrderType.Delivery"
                />
                <span
                    v-if="filteredProducts.length === 0"
                    class="flex justify-center mt-6"
                    v-text="t('noProductsFound')"
                />
                <div v-if="!searchQuery" class="flex items-center space-x-1">
                    <MyCheckbox
                        v-model="showAllProducts"
                        class="mt-4"
                        :label="t('showAllProducts')"
                    />

                    <mdi:information v-tooltip="t('showAllProductsExplanation')" class="self-end" />
                </div>
            </div>
            <div
                v-if="!props.packagingOrderId"
                class="flex flex-col items-start w-full pt-4 space-y-3 !ml-0"
            >
                <MyTextarea
                    v-model="data.comment"
                    name="comment"
                    :label="t('comment')"
                    :placeholder="t('addComment')"
                />
                <MyFileSelect
                    no-border
                    type="image/*,application/pdf"
                    multiple
                    @selected:multiple="uploadFiles"
                >
                    <mdi:attachment class="dark:text-primary-50" />
                </MyFileSelect>
            </div>

            <div v-if="data.files.length > 0" class="flex flex-col mt-3">
                <MyInputLabel v-text="t('files')" />

                <div class="mt-2 grid grid-cols-4 gap-3">
                    <MyFileAttachment
                        v-for="file in data.files"
                        :key="file.id"
                        :file="file"
                        removable
                        :uploading="filesUploading.includes(file.id)"
                        @on-remove-clicked="removeFile(file)"
                    />
                </div>
            </div>
            <div class="mt-4 flex justify-end gap-6">
                <MyButton
                    :disabled="loading"
                    scheme="primary"
                    v-text="props.packagingOrderId ? t('update') : t('create')"
                />
            </div>
        </MyForm>
    </MyModal>
</template>
